import NextImage from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import { Banner, BannerDescription } from '@wartek-id/banner'
import { useToast } from '@wartek-id/toast'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Textarea } from '@wartek-id/textarea'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import { useAPIPostApprovePeriod } from 'api/kinerja/useAPIKinerja'

import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

type MulaiPeriodeCardProps = {
  schools: PelaksanaanSchool[]
  pegawaiLabel: string
  atasanLabel: string
}

export function MulaiPeriodeCard({
  schools,
  pegawaiLabel,
  atasanLabel,
}: MulaiPeriodeCardProps) {
  const toast = useToast()

  // -----------------------------------------
  // Resolve selected period
  // -----------------------------------------
  const { period: selectedPeriod } = usePeriodContext()

  const periodLabel = useMemo(() => {
    if (!selectedPeriod) return ''
    return formatPeriod(selectedPeriod.startDate, selectedPeriod.endDate)
  }, [selectedPeriod])

  // -----------------------------------------
  // Handle start period action
  // -----------------------------------------
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const {
    mutateAsync,
    isLoading: isApproving,
    isSuccess,
  } = useAPIPostApprovePeriod()

  const handleStartPeriod = async () => {
    if (!selectedPeriod) return

    try {
      // Only start period if on confirmed schools
      const confirmedSchools = schools.filter(
        (school) => school.confirmationStatus === 'CONFIRMED'
      )

      // If there is no confirmed school, show error toast and return.
      if (confirmedSchools.length === 0) {
        throw 'Gagal memulai periode. Pastikan Satuan Pendidikan Anda sudah dikonfirmasi.'
      }

      await Promise.all(
        confirmedSchools.map(({ npsn }) =>
          mutateAsync({
            npsn,
            is_approved: true,
            periode_id: selectedPeriod.id,
          })
        )
      )
    } catch (e) {
      toast({ message: e.message || 'Gagal memulai periode. Coba lagi.' })
    } finally {
      setIsDialogOpen(false)
    }
  }

  if (!selectedPeriod) {
    return null
  }

  return (
    <>
      {/* When the period has not yet been started */}
      {!selectedPeriod.isApproved && !isSuccess && (
        <Card>
          <div className="flex gap-5 p-3">
            <NextImage
              src="/icon/menu-pengelolaan-kinerja.svg"
              alt=""
              width={84}
              height={84}
            />

            <div className="space-y-3">
              <div>
                <Text variant="body-bold">
                  Mulai periode {periodLabel} di Satuan Pendidikan Anda
                </Text>
                <Text color="subdued">
                  Mari mulai Pengelolaan Kinerja periode berikutnya. Tenang,
                  periode sebelumnya tetap dapat diakses.
                </Text>
              </div>

              <Button size="sm" onClick={() => setIsDialogOpen(true)}>
                Mulai periode
              </Button>
            </div>
          </div>
        </Card>
      )}

      <ConfirmationDialog
        periodLabel={periodLabel}
        pegawaiLabel={pegawaiLabel}
        atasanLabel={atasanLabel}
        isLoading={isApproving}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleStartPeriod}
      />

      {/* When the period had been successfully started */}
      {isSuccess && (
        <Banner title={`Periode ${periodLabel} sudah dimulai`} type="success">
          <BannerDescription>
            <Text>
              Kami sudah kabari {pegawaiLabel} di satuan pendidikan Anda serta{' '}
              {atasanLabel} dan Dinas Pendidikan. Anda juga dapat ganti periode
              untuk akses periode sebelumnya.
            </Text>
          </BannerDescription>
        </Banner>
      )}
    </>
  )
}

interface ConfirmationDialogProps extends Omit<DialogProps, 'children'> {
  periodLabel: string
  pegawaiLabel: string
  atasanLabel: string
  isLoading?: boolean
  onConfirm: () => void
}

function ConfirmationDialog({
  periodLabel,
  pegawaiLabel,
  atasanLabel,
  isLoading = false,
  onConfirm,
  ...dialogProps
}: ConfirmationDialogProps) {
  const expectedConfirmationText = `Saya setuju untuk mulai Pengelolaan Kinerja periode ${periodLabel}.`

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{
    confirmationText: string
  }>({
    defaultValues: {
      confirmationText: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  useEffect(() => {
    if (!dialogProps.isOpen) {
      reset()
    }
  }, [dialogProps.isOpen, reset])

  return (
    <Dialog {...dialogProps}>
      <DialogOverlay />

      <DialogContent
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <form className="p-6 space-y-4" onSubmit={handleSubmit(onConfirm)}>
          <div className="space-y-4">
            <Text variant="display-sm" as="h2" id="confirmation-dialog-title">
              Mulai periode {periodLabel}?
            </Text>

            <div>
              <Text color="subdued">Jika lanjut:</Text>
              <ol className="list-decimal list-outside ml-6">
                <Text as="li" color="subdued">
                  {pegawaiLabel} dapat mulai perencanaan dan pelaksanaan.
                </Text>
                <Text as="li" color="subdued">
                  {atasanLabel} dan Dinas Pendidikan dapat mulai pemantauan dan
                  penilaian.
                </Text>
              </ol>
            </div>
          </div>

          <Text color="subdued">
            Mohon ketik kalimat ini pada kolom kalimat persetujuan:
          </Text>

          <div className="rounded p-1 bg-blue-5">
            <Text
              variant="heading-sm"
              color="informational"
              className="!text-blue-60 select-none"
            >
              {expectedConfirmationText}
            </Text>
          </div>

          <div className="space-y-1">
            <Text as="label" htmlFor="confirmation-text">
              Kalimat persetujuan
            </Text>

            <Textarea
              id="confirmation-text"
              {...register('confirmationText', {
                validate: (value) =>
                  value === expectedConfirmationText ||
                  'Mohon cek ejaan dan coba lagi.',
              })}
              rows={2}
              isInvalid={Boolean(errors.confirmationText)}
              errorMessage={errors.confirmationText?.message || ''}
            ></Textarea>
          </div>

          <div className="flex gap-5">
            <Button
              type="button"
              onClick={() => dialogProps?.onClose?.()}
              color="white"
              size="lg"
            >
              Kembali
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
              color="blue"
              size="lg"
            >
              Lanjut
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
